import { useState, useCallback, useRef } from "react"
import { Message, User, CreateMessagePartRequest, DisplayMessage } from "../ZeefTypes"
import ZeefService from "../ZeefService"

interface UseZeefMessagesReturn {
  // State
  messages: DisplayMessage[]
  inputMessage: string
  isLoadingMessages: boolean
  isLoading: boolean
  replyingToMessage: DisplayMessage | null
  threads: Map<string, Message[]>
  isBroadcastMode: boolean

  // Refs
  messageInputRef: React.RefObject<HTMLInputElement>
  messagesContainerRef: React.RefObject<HTMLDivElement>

  // Actions
  loadMessageHistory: (browserLoadTime: string) => Promise<void>
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleSendMessage: (event: React.FormEvent, currentUser: User | null, socialUsers: User[]) => Promise<void>
  handleReplyToMessage: (message: DisplayMessage) => void
  handleCancelReply: () => void
  toggleBroadcastMode: () => void
  focusInput: () => void
  scrollToBottom: () => void
  addMessage: (message: DisplayMessage) => void
  updateMessage: (messageId: string, updates: Partial<DisplayMessage>) => void
  calculateThreadDepths: (messages: DisplayMessage[]) => Map<string, number>
}

export const useZeefMessages = (): UseZeefMessagesReturn => {
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [inputMessage, setInputMessage] = useState("")
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [replyingToMessage, setReplyingToMessage] = useState<DisplayMessage | null>(null)
  const [threads, setThreads] = useState<Map<string, Message[]>>(new Map())
  const [isBroadcastMode, setIsBroadcastMode] = useState(false)

  const messageInputRef = useRef<HTMLInputElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const loadMessageHistory = useCallback(async (browserLoadTime: string) => {
    if (isLoadingMessages) return

    try {
      setIsLoadingMessages(true)
      const response = await ZeefService.getMessages({ page: 1, limit: 50 })

      // Fetch complete message details
      const detailedMessages = await Promise.all(
        response.items.map(async (message) => {
          try {
            return await ZeefService.getMessageDetails(message.id)
          } catch (error) {
            console.error(`Failed to fetch details for message ${message.id}:`, error)
            return message
          }
        }),
      )

      // Convert to display format
      const formattedMessages: DisplayMessage[] = detailedMessages.map((message) => ({
        id: message.id,
        type: "received",
        content: ZeefService.getMessageDisplayText(message),
        timestamp: message.created_at,
        sender: message.sender,
        zeefMessage: message,
        isHistorical: new Date(message.created_at) < new Date(browserLoadTime),
        isBroadcast: message.type === "broadcast",
        thread_root_id: message.thread_root_id,
        serverId: message.id,
      }))

      setMessages(formattedMessages.reverse())
      setIsLoadingMessages(false)
    } catch (error) {
      console.error("Failed to load message history:", error)
      setIsLoadingMessages(false)
    }
  }, [isLoadingMessages])

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputMessage(event.target.value)
  }, [])

  const parseMentions = useCallback((text: string, socialUsers: User[]): CreateMessagePartRequest[] => {
    const mentionRegex = /@(\w+)/g
    const parts: CreateMessagePartRequest[] = []
    let lastIndex = 0
    let match

    const findUserByUsername = (username: string): User | null => {
      return socialUsers.find((user) => user.username.toLowerCase() === username.toLowerCase()) || null
    }

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          content: text.substring(lastIndex, match.index),
          order: parts.length,
        })
      }

      // Find user by username
      const username = match[1]
      const mentionedUser = findUserByUsername(username)

      if (mentionedUser) {
        parts.push({
          type: "mention",
          user_id: mentionedUser.id,
          display_name: `@${username}`,
          order: parts.length,
        })
      } else {
        // If user not found, treat as text
        parts.push({
          type: "text",
          content: match[0],
          order: parts.length,
        })
      }

      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex),
        order: parts.length,
      })
    }

    // If no mentions found, return simple text part
    if (parts.length === 0) {
      parts.push({
        type: "text",
        content: text,
        order: 0,
      })
    }

    return parts
  }, [])

  const handleSendMessage = useCallback(async (
    event: React.FormEvent,
    currentUser: User | null,
    socialUsers: User[]
  ) => {
    event.preventDefault()

    if (!inputMessage.trim() || isLoading || !currentUser) return

    // Determine the correct thread root ID for threading
    const getThreadRootId = (message: DisplayMessage | null) => {
      if (!message) return null
      if (message.thread_root_id) return message.thread_root_id
      return message.serverId || message.id
    }

    const threadRootId = getThreadRootId(replyingToMessage)

    const userMessage: DisplayMessage = {
      id: Date.now().toString(),
      type: "user",
      content: inputMessage,
      timestamp: new Date().toISOString(),
      sender: currentUser,
      isHistorical: false,
      isBroadcast: isBroadcastMode,
      thread_root_id: threadRootId,
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage("")
    setIsLoading(true)
    setReplyingToMessage(null)

    try {
      const messageType: "broadcast" | "regular" = isBroadcastMode ? "broadcast" : "regular"
      const parts = parseMentions(inputMessage, socialUsers)

      const messageRequest = {
        type: messageType,
        thread_root_id: threadRootId,
        parts: parts,
      }

      const sentMessage = await ZeefService.sendMessage(messageRequest)

      // Update the local message with the server response data
      setMessages(prev => prev.map((msg) => {
        if (
          msg.type === "user" &&
          msg.content === inputMessage &&
          !msg.serverId &&
          msg.thread_root_id === threadRootId
        ) {
          return {
            ...msg,
            id: sentMessage.id,
            serverId: sentMessage.id,
            zeefMessage: sentMessage,
            timestamp: sentMessage.created_at,
          }
        }
        return msg
      }))

      setIsLoading(false)
      focusInput()
    } catch (error) {
      console.error("Failed to send message:", error)

      const errorMessage: DisplayMessage = {
        id: Date.now().toString(),
        type: "error",
        content: "Failed to send message. Please try again.",
        timestamp: new Date().toISOString(),
        isHistorical: false,
      }

      setMessages(prev => [...prev, errorMessage])
      setIsLoading(false)
      setReplyingToMessage(null)
      focusInput()
    }
  }, [inputMessage, isLoading, replyingToMessage, isBroadcastMode, parseMentions])

  const handleReplyToMessage = useCallback((message: DisplayMessage) => {
    setReplyingToMessage(message)
    focusInput()
  }, [])

  const handleCancelReply = useCallback(() => {
    setReplyingToMessage(null)
    focusInput()
  }, [])

  const toggleBroadcastMode = useCallback(() => {
    setIsBroadcastMode(prev => !prev)
  }, [])

  const focusInput = useCallback(() => {
    if (messageInputRef.current) {
      setTimeout(() => {
        messageInputRef.current?.focus()
      }, 100)
    }
  }, [])

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const messagesContent = messagesContainerRef.current.querySelector(".messages-content")
      if (messagesContent) {
        messagesContent.scrollTop = messagesContent.scrollHeight
      }
    }
  }, [])

  const addMessage = useCallback((message: DisplayMessage) => {
    setMessages(prev => [...prev, message])
  }, [])

  const updateMessage = useCallback((messageId: string, updates: Partial<DisplayMessage>) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, ...updates } : msg
    ))
  }, [])

  const calculateThreadDepths = useCallback((messages: DisplayMessage[]): Map<string, number> => {
    const threadDepths = new Map<string, number>()

    messages.forEach((message) => {
      const messageId = message.serverId || message.id

      if (!message.thread_root_id || message.thread_root_id === messageId) {
        threadDepths.set(messageId, 0)
      } else {
        threadDepths.set(messageId, 1)
      }
    })

    return threadDepths
  }, [])

  return {
    // State
    messages,
    inputMessage,
    isLoadingMessages,
    isLoading,
    replyingToMessage,
    threads,
    isBroadcastMode,

    // Refs
    messageInputRef,
    messagesContainerRef,

    // Actions
    loadMessageHistory,
    handleInputChange,
    handleSendMessage,
    handleReplyToMessage,
    handleCancelReply,
    toggleBroadcastMode,
    focusInput,
    scrollToBottom,
    addMessage,
    updateMessage,
    calculateThreadDepths,
  }
}
